if (!customElements.get("sc-quantity-discount-slider")) {
  customElements.define(
    "sc-quantity-discount-slider",
    class ScQuantityDiscountSlider extends HTMLElement {
      constructor() {
        super();

        this.rangeInput = this.querySelector("[data-sc-quantity-range]");
        this.minusButton = this.querySelector("[data-sc-quantity-minus]");
        this.plusButton = this.querySelector("[data-sc-quantity-plus]");
        this.tierButtons = Array.from(
          this.querySelectorAll("[data-sc-tier-button]"),
        );
        this.quantityDisplayInput = this.querySelector(
          "[data-sc-quantity-display]",
        );
        this.quantityInput = this.querySelector("[data-sc-quantity-input]");
        this.variantIdInput = this.querySelector("[data-sc-variant-id-input]");
        this.discountLabelInput = this.querySelector(
          "[data-sc-discount-label-input]",
        );
        this.discountTypeInput = this.querySelector(
          "[data-sc-discount-type-input]",
        );
        this.discountValueInput = this.querySelector(
          "[data-sc-discount-value-input]",
        );
        this.quantityValue = this.querySelector("[data-sc-quantity-value]");
        this.discountLabel = this.querySelector("[data-sc-discount-label]");
        this.unitPrice = this.querySelector("[data-sc-unit-price]");
        this.totalPrice = this.querySelector("[data-sc-total-price]");

        this.basePrice = Number(this.dataset.basePrice || 0);
        this.baseCompareAtPrice = Number(this.dataset.baseCompareAtPrice || 0);
        this.variantId = this.dataset.variantId || "";
        this.productFormId = this.dataset.productFormId || "";
        this.sectionId = this.dataset.sectionId || "";
        this.productId = this.dataset.productId || "";
        this.discountMode = this.dataset.scDiscountMode || "percent";
        this.isSyncingQuantity = false;
        this.externalQuantityUnsubscribers = [];

        this.tiers = this.getTiers();
        if (this.tiers.length === 0) {
          return;
        }
        this.currentQuantity = this.tiers[0].quantity;

        this.setupControlBounds();
        this.bindControls();
        this.bindExternalQuantitySync();

        this.variantChangeUnsubscriber = undefined;
        this.subscribeToVariantChanges();
        this.syncFromExternalQuantity();
        this.updateUI();
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
        this.externalQuantityUnsubscribers.forEach((unsubscribe) =>
          unsubscribe(),
        );
        this.externalQuantityUnsubscribers = [];
      }

      getTiers() {
        const tiersElement = this.querySelector("[data-sc-tiers]");
        if (!tiersElement) return [];

        try {
          const tiers = JSON.parse(tiersElement.textContent || "[]");
          return tiers
            .filter((tier) => Number(tier.quantity) > 0)
            .map((tier) => ({
              quantity: Number(tier.quantity),
              discount: Number(tier.discount || 0),
              label: tier.label || "",
            }))
            .sort((a, b) => a.quantity - b.quantity);
        } catch (error) {
          return [];
        }
      }

      setupControlBounds() {
        const minQuantity = this.tiers[0].quantity;
        const maxQuantity = this.tiers[this.tiers.length - 1].quantity;
        if (this.rangeInput) {
          this.rangeInput.min = String(minQuantity);
          this.rangeInput.max = String(maxQuantity);
          this.rangeInput.step = "1";
        }
        if (this.quantityDisplayInput) {
          this.quantityDisplayInput.min = String(minQuantity);
          this.quantityDisplayInput.max = String(maxQuantity);
          this.quantityDisplayInput.step = "1";
        }

        const currentValue = this.getCurrentQuantity();
        if (currentValue < minQuantity || currentValue > maxQuantity) {
          this.setCurrentQuantity(minQuantity);
        }
      }

      bindControls() {
        if (this.rangeInput) {
          this.rangeInput.addEventListener("input", () => this.updateUI());
          this.rangeInput.addEventListener("change", () => this.updateUI());
        }

        if (this.quantityDisplayInput) {
          this.quantityDisplayInput.addEventListener("input", () =>
            this.updateUI(),
          );
          this.quantityDisplayInput.addEventListener("change", () =>
            this.updateUI(),
          );
        }

        if (this.minusButton) {
          this.minusButton.addEventListener("click", () => {
            this.setCurrentQuantity(this.getCurrentQuantity() - 1);
            this.updateUI();
          });
        }

        if (this.plusButton) {
          this.plusButton.addEventListener("click", () => {
            this.setCurrentQuantity(this.getCurrentQuantity() + 1);
            this.updateUI();
          });
        }

        if (this.tierButtons.length > 0) {
          this.tierButtons.forEach((button) => {
            button.addEventListener("click", () => {
              const tierQuantity = Number(button.dataset.scTierQuantity || 0);
              if (tierQuantity > 0) {
                this.setCurrentQuantity(tierQuantity);
                this.updateUI();
              }
            });
          });
        }
      }

      getFormQuantityInputs() {
        if (!this.productFormId) return [];

        return Array.from(
          document.querySelectorAll(
            `input[name="quantity"][form="${this.productFormId}"]`,
          ),
        );
      }

      getExternalQuantityInputs() {
        return this.getFormQuantityInputs().filter(
          (input) => input !== this.quantityInput,
        );
      }

      bindExternalQuantitySync() {
        const externalInputs = this.getExternalQuantityInputs();
        externalInputs.forEach((input) => {
          const handler = () => {
            if (this.isSyncingQuantity) return;
            this.syncFromExternalQuantity();
            this.updateUI();
          };
          input.addEventListener("input", handler);
          input.addEventListener("change", handler);
          this.externalQuantityUnsubscribers.push(() => {
            input.removeEventListener("input", handler);
            input.removeEventListener("change", handler);
          });
        });
      }

      syncFromExternalQuantity() {
        const externalInputs = this.getExternalQuantityInputs();
        if (externalInputs.length === 0) return;

        const externalInput = externalInputs[0];
        const quantity = Number(externalInput.value || 0);
        if (quantity > 0) {
          this.setCurrentQuantity(quantity);
        }
      }

      getCurrentQuantity() {
        const fallbackQuantity = this.currentQuantity || this.tiers[0].quantity;

        if (this.quantityDisplayInput) {
          const value = Number(this.quantityDisplayInput.value || fallbackQuantity);
          return Number.isNaN(value) ? fallbackQuantity : value;
        }

        if (this.rangeInput) {
          const value = Number(this.rangeInput.value || fallbackQuantity);
          return Number.isNaN(value) ? fallbackQuantity : value;
        }

        return fallbackQuantity;
      }

      setCurrentQuantity(quantity) {
        const minQuantity = this.tiers[0].quantity;
        const maxQuantity = this.tiers[this.tiers.length - 1].quantity;
        const safeQuantity = Math.min(
          maxQuantity,
          Math.max(minQuantity, Number(quantity) || minQuantity),
        );
        this.currentQuantity = safeQuantity;

        if (this.quantityDisplayInput) {
          this.quantityDisplayInput.value = String(safeQuantity);
        }
        if (this.rangeInput) {
          this.rangeInput.value = String(safeQuantity);
        }
        if (this.quantityInput) {
          this.quantityInput.value = String(safeQuantity);
        }
      }

      getCurrentTier(quantity) {
        let matchedTier = this.tiers[0];

        for (const tier of this.tiers) {
          if (quantity >= tier.quantity) {
            matchedTier = tier;
          }
        }

        return matchedTier;
      }

      subscribeToVariantChanges() {
        if (
          !window.theme ||
          !theme.pubsub ||
          !theme.pubsub.subscribe ||
          !theme.pubsub.PUB_SUB_EVENTS
        ) {
          return;
        }

        this.variantChangeUnsubscriber = theme.pubsub.subscribe(
          theme.pubsub.PUB_SUB_EVENTS.variantChange,
          (event) => {
            const variant = event?.data?.variant;
            if (!variant) return;

            this.basePrice = Number(variant.price || 0);
            this.baseCompareAtPrice = Number(variant.compare_at_price || 0);
            this.variantId = String(variant.id || "");

            if (this.variantIdInput && this.variantId) {
              this.variantIdInput.value = this.variantId;
            }

            this.updateUI();
          },
        );
      }

      formatMoney(amount) {
        const format = theme?.settings?.currencyCodeEnabled
          ? theme.settings.moneyWithCurrencyFormat
          : theme.settings.moneyFormat;

        if (theme?.Currency?.formatMoney) {
          return theme.Currency.formatMoney(amount, format);
        }

        return `${(amount / 100).toFixed(2)} €`;
      }

      formatMoneyPlain(amount) {
        const html = this.formatMoney(amount);
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        return (doc.body.textContent || "").trim();
      }

      updateUI() {
        this.setCurrentQuantity(this.getCurrentQuantity());
        const quantity = this.getCurrentQuantity();
        const tier = this.getCurrentTier(quantity);
        const discountValue = Number(tier.discount || 0);
        const isAmountDiscount = this.discountMode === "amount";
        const discountedUnitPrice = isAmountDiscount
          ? Math.max(0, this.basePrice - Math.round(discountValue * 100))
          : Math.round(this.basePrice * Math.max(0, 1 - discountValue / 100));
        const total = discountedUnitPrice * quantity;

        if (this.quantityInput) {
          this.quantityInput.value = String(quantity);
        }
        this.syncMainProductQuantity(quantity);

        if (this.quantityValue) {
          this.quantityValue.textContent = String(quantity);
        }

        if (this.discountLabel) {
          const labelPrefix = tier.label ? `${tier.label} - ` : "";
          const discountText = isAmountDiscount
            ? `${this.formatMoneyPlain(Math.round(discountValue * 100))} Rabatt`
            : `${discountValue}% Rabatt`;
          this.discountLabel.textContent = `${labelPrefix}${discountText}`;
        }
        if (this.discountLabelInput) {
          this.discountLabelInput.value = tier.label || "";
        }
        if (this.discountTypeInput) {
          this.discountTypeInput.value = this.discountMode;
        }
        if (this.discountValueInput) {
          this.discountValueInput.value = String(discountValue || 0);
        }
        this.updateTierButtons(tier);

        if (this.unitPrice) {
          this.unitPrice.innerHTML = this.formatMoney(discountedUnitPrice);
        }
        this.updateDisplayedProductPrice(discountedUnitPrice);

        if (this.totalPrice) {
          this.totalPrice.innerHTML = this.formatMoney(total);
        }
      }

      syncMainProductQuantity(quantity) {
        if (!this.productFormId) return;

        this.isSyncingQuantity = true;
        this.getFormQuantityInputs().forEach((input) => {
          if (input !== this.quantityInput) {
            input.value = String(quantity);
            input.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });
        this.isSyncingQuantity = false;
      }

      updateDisplayedProductPrice(discountedUnitPrice) {
        if (!this.sectionId || !this.productId) return;

        const priceContainers = document.querySelectorAll(
          `#Price-${this.sectionId}-${this.productId} .price`,
        );
        if (priceContainers.length === 0) return;

        priceContainers.forEach((container) => {
          const regularElement = container.querySelector(".price__regular");
          if (regularElement) {
            regularElement.innerHTML = this.formatMoney(discountedUnitPrice);
          }

          const saleElement = container.querySelector(".price__sale");
          if (!saleElement) return;

          const comparePrice = this.baseCompareAtPrice || this.basePrice;
          if (comparePrice > discountedUnitPrice) {
            container.classList.add("price--on-sale");
            saleElement.innerHTML = this.formatMoney(comparePrice);
            saleElement.style.display = "";
          } else {
            container.classList.remove("price--on-sale");
            saleElement.style.display = "none";
          }
        });
      }

      updateTierButtons(tier) {
        if (this.tierButtons.length === 0) return;

        this.tierButtons.forEach((button) => {
          const tierQuantity = Number(button.dataset.scTierQuantity || 0);
          const isActive = tierQuantity === tier.quantity;
          button.setAttribute("aria-pressed", isActive ? "true" : "false");
          button.classList.toggle("is-active", isActive);
        });
      }
    },
  );
}
